import React, { useCallback, useEffect, useMemo, useState } from "react"
import StatusPill from "../shared/components/StatusPill"
import SectionCard from "../shared/components/SectionCard"
import { ui } from "../shared/styles/uiSystem"
import { apiFetch } from "../utils/apiFetch"
import {
  buttonStyle,
  primaryButtonStyle,
  dangerButtonStyle,
} from "../features/clinical-history/components/ClinicalHistoryStyles"
import {
  SummaryTile,
  EmptyStateBlock,
} from "../features/clinical-history/components/ClinicalHistoryUiBlocks"
import {
  getCaseStatusLabel,
  getCaseStatusTone,
  getEvolutionStatusLabel,
} from "../features/clinical-history/components/ClinicalHistoryStatusHelpers"

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

const SPECIALTY_META = {
  ENDO: {
    title: "Endodoncia",
    description: "Detalle del caso clÃ­nico endodÃ³ntico.",
    tone: "warning",
  },
  PERIO: {
    title: "Periodoncia",
    description: "Detalle del caso clÃ­nico periodontal.",
    tone: "success",
  },
  SURGERY: {
    title: "CirugÃ­a",
    description: "Detalle del caso clÃ­nico quirÃºrgico.",
    tone: "danger",
  },
  ORTHO: {
    title: "Ortodoncia",
    description: "Detalle del caso ortodÃ³ntico.",
    tone: "info",
  },
  PEDIATRIC: {
    title: "OdontopediatrÃ­a",
    description: "Detalle del caso clÃ­nico pediÃ¡trico.",
    tone: "success",
  },
}

function EvolutionRow({ item }) {
  return (
    <div
      style={{
        ...ui.card,
        borderRadius: 16,
        padding: 14,
        display: "grid",
        gap: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <StatusPill tone="info">EvoluciÃ³n {item.evolutionNumber}</StatusPill>

        <StatusPill tone="neutral">
          {item.date
            ? new Date(item.date).toLocaleDateString("es-MX")
            : "Sin fecha"}
        </StatusPill>

        <StatusPill tone="neutral">
          {getEvolutionStatusLabel(item.status)}
        </StatusPill>
      </div>

      <div
        style={{
          fontSize: 15,
          fontWeight: 900,
          color: "#0f172a",
          lineHeight: 1.2,
        }}
      >
        {item.title || "Sin tÃ­tulo"}
      </div>

      <div
        style={{
          fontSize: 13.5,
          color: "#475569",
          lineHeight: 1.55,
        }}
      >
        {item.summary || "Sin resumen clÃ­nico registrado."}
      </div>
    </div>
  )
}

export default function ClinicalHistorySpecialtyCasePage({
  clinicId,
  caseId,
  specialtyType,
  patientDisplayName,
  onBackToCases,
  onOpenNewEvolution,
}) {
  const meta = SPECIALTY_META[specialtyType] || SPECIALTY_META.ENDO

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [caseDetail, setCaseDetail] = useState(null)
  const [evolutions, setEvolutions] = useState([])
  const [closing, setClosing] = useState(false)

  const loadCase = useCallback(async () => {
    try {
      setLoading(true)
      setError("")

      const [detailRes, evolutionsRes] = await Promise.all([
        apiFetch(`${API_BASE}/clinical-specialty-cases/${caseId}?clinicId=${clinicId}`),
        apiFetch(
          `${API_BASE}/clinical-specialty-cases/${caseId}/evolutions?clinicId=${clinicId}`
        ),
      ])

      const detailData = await detailRes.json().catch(() => null)
      const evolutionsData = await evolutionsRes.json().catch(() => null)

      if (!detailRes.ok) {
        throw new Error(detailData?.message || "No se pudo cargar el detalle del caso.")
      }

      if (!evolutionsRes.ok) {
        throw new Error(
          evolutionsData?.message || "No se pudieron cargar las evoluciones."
        )
      }

      setCaseDetail(detailData)
      setEvolutions(Array.isArray(evolutionsData) ? evolutionsData : [])
    } catch (err) {
      setError(err?.message || "No se pudo cargar el caso clÃ­nico.")
    } finally {
      setLoading(false)
    }
  }, [caseId, clinicId])

  useEffect(() => {
    if (!caseId || !clinicId) return
    loadCase()
  }, [caseId, clinicId, loadCase])

  const teethLabel = useMemo(() => {
    if (!caseDetail?.teeth?.length) return "Sin pieza definida"

    return caseDetail.teeth
      .map((item) =>
        item.surface && item.surface !== "ALL"
          ? `${item.tooth}-${item.surface}`
          : item.tooth
      )
      .join(", ")
  }, [caseDetail])

  const isClosed = useMemo(() => {
    return ["COMPLETED", "CANCELLED"].includes(caseDetail?.status || "")
  }, [caseDetail])

  const isFollowUp = useMemo(() => {
    return caseDetail?.status === "FOLLOW_UP"
  }, [caseDetail])

  const isDraft = useMemo(() => {
    return caseDetail?.status === "DRAFT"
  }, [caseDetail])

  const handleCloseCase = async () => {
    try {
      setClosing(true)

      const res = await apiFetch(
        `${API_BASE}/clinical-specialty-cases/${caseId}/status?clinicId=${clinicId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: "COMPLETED",
          }),
        }
      )

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        throw new Error(data?.message || "No se pudo cerrar el caso.")
      }

      await loadCase()
    } catch (err) {
      alert(err?.message || "No se pudo cerrar el caso.")
    } finally {
      setClosing(false)
    }
  }

  const handleSetFollowUp = async () => {
    try {
      const res = await apiFetch(
        `${API_BASE}/clinical-specialty-cases/${caseId}/status?clinicId=${clinicId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: "FOLLOW_UP",
          }),
        }
      )

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        throw new Error(data?.message || "No se pudo pasar el caso a seguimiento.")
      }

      await loadCase()
    } catch (err) {
      alert(err?.message || "No se pudo pasar el caso a seguimiento.")
    }
  }

  const handleReactivateCase = async () => {
    try {
      const res = await apiFetch(
        `${API_BASE}/clinical-specialty-cases/${caseId}/status?clinicId=${clinicId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: "IN_PROGRESS",
          }),
        }
      )

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        throw new Error(data?.message || "No se pudo reactivar el caso.")
      }

      await loadCase()
    } catch (err) {
      alert(err?.message || "No se pudo reactivar el caso.")
    }
  }

  const latestActivity = caseDetail?.lastActivityAt
    ? new Date(caseDetail.lastActivityAt).toLocaleDateString("es-MX")
    : "Sin actividad"

  return (
    <div style={{ padding: 24, background: "#f8fafc", minHeight: "100vh" }}>
      <div
        style={{
          display: "grid",
          gap: 18,
          alignContent: "start",
        }}
      >
        <div
          style={{
            ...ui.card,
            borderRadius: 22,
            padding: 18,
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 18,
              alignItems: "stretch",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                minWidth: 280,
                flex: "1 1 520px",
                display: "grid",
                gap: 10,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "#64748b",
                }}
              >
                Caso clÃ­nico
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 900,
                    color: "#0f172a",
                    lineHeight: 1.02,
                    letterSpacing: "-0.03em",
                    wordBreak: "break-word",
                  }}
                >
                  {caseDetail?.title || `${meta.title} Â· Caso`}
                </div>

                <StatusPill tone={getCaseStatusTone(caseDetail?.status)}>
                  {getCaseStatusLabel(caseDetail?.status || "")}
                </StatusPill>
              </div>

              <div
                style={{
                  fontSize: 13,
                  color: "#64748b",
                  lineHeight: 1.45,
                  maxWidth: 760,
                }}
              >
                {meta.description} Paciente actual:{" "}
                <strong>{patientDisplayName}</strong>.
              </div>
            </div>

            <div
              style={{
                minWidth: 260,
                flex: "1 1 300px",
                display: "grid",
                gap: 10,
                alignContent: "start",
              }}
            >
              <SummaryTile
                title="Ãšltima actividad"
                value={latestActivity}
                subtitle="Movimiento mÃ¡s reciente del caso"
                tone="info"
              />

              <SummaryTile
                title="Evoluciones"
                value={String(evolutions.length)}
                subtitle="Registros dentro del caso"
                tone={evolutions.length > 0 ? "success" : "neutral"}
              />
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              justifyContent: "flex-end",
              marginTop: 16,
            }}
          >
            <button onClick={onBackToCases} style={buttonStyle}>
              Volver a casos
            </button>

            {!isClosed ? (
              <button onClick={onOpenNewEvolution} style={primaryButtonStyle}>
                {isDraft ? "Registrar primera evoluciÃ³n" : "Registrar evoluciÃ³n"}
              </button>
            ) : null}

            {!isClosed && !isFollowUp ? (
              <button onClick={handleSetFollowUp} style={buttonStyle}>
                Pasar a seguimiento
              </button>
            ) : null}

            {!isClosed && isFollowUp ? (
              <button onClick={handleReactivateCase} style={buttonStyle}>
                Reactivar caso
              </button>
            ) : null}

            {!isClosed ? (
              <button
                onClick={handleCloseCase}
                disabled={closing}
                style={{
                  ...dangerButtonStyle,
                  cursor: closing ? "not-allowed" : "pointer",
                  opacity: closing ? 0.75 : 1,
                }}
              >
                {closing ? "Cerrando..." : "Cerrar caso"}
              </button>
            ) : null}
          </div>
        </div>

        {loading ? (
          <EmptyStateBlock text="Cargando detalle del caso..." />
        ) : error ? (
          <EmptyStateBlock text={error} />
        ) : (
          <>
            <SectionCard
              title="Resumen del caso"
              subtitle="Contexto clÃ­nico principal del episodio"
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: 12,
                }}
              >
                <SummaryTile
                  title="Estado"
                  value={getCaseStatusLabel(caseDetail?.status || "")}
                  subtitle="Estado actual del caso clÃ­nico"
                  tone={isClosed ? "neutral" : "info"}
                />

                <SummaryTile
                  title="Piezas"
                  value={teethLabel}
                  subtitle="Pieza o superficies asociadas"
                  tone="neutral"
                />

                <SummaryTile
                  title="Prioridad"
                  value={caseDetail?.priority || "No definida"}
                  subtitle="Nivel clÃ­nico asignado"
                  tone={caseDetail?.priority === "ALTA" ? "danger" : "neutral"}
                />

                <SummaryTile
                  title="Apertura"
                  value={
                    caseDetail?.openedAt
                      ? new Date(caseDetail.openedAt).toLocaleDateString("es-MX")
                      : "Sin fecha"
                  }
                  subtitle="Fecha de apertura del caso"
                  tone="info"
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                  gap: 12,
                  marginTop: 14,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      marginBottom: 6,
                    }}
                  >
                    Motivo clÃ­nico
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      color: "#334155",
                      lineHeight: 1.6,
                    }}
                  >
                    {caseDetail?.chiefReason || "Sin motivo clÃ­nico registrado."}
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      marginBottom: 6,
                    }}
                  >
                    Origen
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      color: "#334155",
                      lineHeight: 1.6,
                    }}
                  >
                    {caseDetail?.origin || "MANUAL"}
                  </div>
                </div>
              </div>
            </SectionCard>

            <SectionCard
              title="Evoluciones"
              subtitle="Movimientos clÃ­nicos registrados dentro del caso"
            >
              {evolutions.length ? (
                <div
                  style={{
                    display: "grid",
                    gap: 10,
                  }}
                >
                  {evolutions.map((item) => (
                    <EvolutionRow key={item.id} item={item} />
                  ))}
                </div>
              ) : (
                <EmptyStateBlock text="TodavÃ­a no hay evoluciones registradas para este caso." />
              )}
            </SectionCard>
          </>
        )}
      </div>
    </div>
  )
}
